const STEPS = [
  {
    num: "01", title: "First Launch",
    badge: "bg-violet-500/20 text-violet-300 border-violet-500/40",
    acc: "text-violet-300",
    fill: "rgba(167,139,248,.1)", stroke: "rgba(167,139,248,.4)",
    desc: "User registers a master password. Argon2id KDF (64 MB, 3 passes) derives a vault key. The key is wrapped with AES-256-GCM and optionally with Windows DPAPI for the current Windows SID.",
    artifact: "Encrypted vault key on disk",
  },
  {
    num: "02", title: "Unlock",
    badge: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    acc: "text-cyan-300",
    fill: "rgba(34,211,238,.1)", stroke: "rgba(34,211,238,.4)",
    desc: "On relaunch, DPAPI silently unwraps the vault key for the registered Windows account — no password prompt. Other users fall back to the master password path. Key lives in a zeroed Python bytearray in memory only.",
    artifact: "In-memory vault key (never on disk)",
  },
  {
    num: "03", title: "Credential Storage",
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    acc: "text-emerald-300",
    fill: "rgba(52,211,153,.1)", stroke: "rgba(52,211,153,.4)",
    desc: "Every credential is encrypted individually with AES-256-GCM using a unique nonce before being written to a local SQLite database. No credential is ever stored in plaintext.",
    artifact: "SQLite DB — per-entry AES-256-GCM ciphertext",
  },
  {
    num: "04", title: "Browser Autofill",
    badge: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    acc: "text-sky-300",
    fill: "rgba(56,189,248,.1)", stroke: "rgba(56,189,248,.4)",
    desc: "The browser extension detects a login form and queries the native host via Chrome Native Messaging. The desktop app decrypts the matching credential in memory and returns it. A floating button triggers one-click field fill.",
    artifact: "Decrypted credential (in memory, never persisted)",
  },
  {
    num: "05", title: "Capture & Save",
    badge: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    acc: "text-amber-300",
    fill: "rgba(251,191,36,.1)", stroke: "rgba(251,191,36,.4)",
    desc: "The content script monitors form submissions and survives redirects via sessionStorage. On the next page load, it checks the vault — showing a Save banner for new credentials or an Update banner when a changed password is detected.",
    artifact: "New or updated encrypted credential in SQLite",
  },
  {
    num: "06", title: "Lock",
    badge: "bg-red-500/20 text-red-300 border-red-500/40",
    acc: "text-red-300",
    fill: "rgba(248,113,113,.1)", stroke: "rgba(248,113,113,.4)",
    desc: "On lock, the in-memory vault key bytearray is zeroed immediately. The clipboard is auto-cleared after 30 seconds. No plaintext credential data survives a lock event.",
    artifact: "Zero-wiped memory — no plaintext persists",
  },
];

export default function VaultDataFlow() {
  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">DATA FLOW</p>
        <h4 className="mt-1 text-sm font-semibold text-white">End-to-end vault lifecycle</h4>
        <p className="mt-1 text-xs text-slate-400">
          From first launch through unlock, storage, autofill, capture, and lock — every stage is designed so plaintext credential data never touches disk.
        </p>
      </div>

      {/* Flow SVG */}
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-black/30 p-4">
        <svg viewBox="0 0 700 100" width="100%" style={{ minWidth: 560 }}>
          <defs>
            <marker id="sv-arr" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={5} markerHeight={5} orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="rgba(255,255,255,.2)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </marker>
          </defs>
          {STEPS.map((s, i) => {
            const x = 10 + i * 116;
            return (
              <g key={s.num}>
                <rect x={x} y={15} width={100} height={56} rx={7} fill={s.fill} stroke={s.stroke} strokeWidth={0.8} />
                <text x={x + 50} y={32} textAnchor="middle" fontSize={9} fontFamily="monospace" fill="rgba(255,255,255,.3)">{s.num}</text>
                <text x={x + 50} y={47} textAnchor="middle" fontSize={9.5} fontWeight={600} fill="#f0f4ff" fontFamily="system-ui">{s.title}</text>
                <text x={x + 50} y={61} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,.3)" fontFamily="system-ui">
                  {s.artifact.length > 22 ? s.artifact.slice(0, 20) + "…" : s.artifact}
                </text>
                {i < STEPS.length - 1 && (
                  <line x1={x + 100} y1={43} x2={x + 116} y2={43} stroke="rgba(255,255,255,.15)" strokeWidth={1.2} markerEnd="url(#sv-arr)" />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Step cards */}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {STEPS.map((s) => (
          <div key={s.num} className="rounded-xl border border-white/10 bg-white/5 p-3">
            <div className="mb-1.5 flex items-center gap-2">
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border font-mono text-[9px] font-bold ${s.badge}`}>
                {s.num}
              </span>
              <p className="text-xs font-semibold text-white">{s.title}</p>
            </div>
            <p className="text-[11px] leading-relaxed text-slate-400">{s.desc}</p>
            <p className={`mt-2 font-mono text-[9px] ${s.acc}`}>→ {s.artifact}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
